import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { User, Wrench, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function RoleSelect() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) navigate("/login", { replace: true });
    else if (user.role) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  if (!user || user.role) return null;

  const pick = async (role) => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/role", { role });
      setUser(data);
      toast.success(`Welcome, ${role === "worker" ? "Pro" : "Customer"}!`);
      navigate(role === "worker" ? "/worker/onboarding" : "/dashboard", { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not set role");
      // If role is already set, route to dashboard
      if (e?.response?.status === 400) {
        navigate("/dashboard", { replace: true });
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-3">Step 01 / Account Type</div>
        <h1 className="font-display text-4xl sm:text-5xl mb-3">Welcome, {user.name?.split(" ")[0]}.</h1>
        <p className="text-base text-slate-600 mb-12 max-w-xl">
          Choose how you&apos;ll use CanHireSolution.com. You can adjust this later in settings.
        </p>

        <div className="grid md:grid-cols-2 gap-6">
          <button
            onClick={() => pick("customer")}
            disabled={busy}
            className="text-left p-8 border-2 border-slate-200 hover:border-brand hover:-translate-y-1 transition-all bg-white group"
            data-testid="role-customer"
          >
            <div className="w-12 h-12 bg-slate-900 text-white grid place-items-center mb-6">
              <User size={24} weight="bold" />
            </div>
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-2">For Customers</div>
            <h3 className="font-display text-2xl mb-2">I need work done</h3>
            <p className="text-sm text-slate-600 mb-6">Post jobs, get matched with vetted Canadian pros, chat, and pay safely.</p>
            <div className="flex items-center gap-2 text-sm font-semibold text-brand group-hover:gap-3 transition-all">
              Continue <ArrowRight size={16} weight="bold" />
            </div>
          </button>

          <button
            onClick={() => pick("worker")}
            disabled={busy}
            className="text-left p-8 border-2 border-slate-200 hover:border-brand hover:-translate-y-1 transition-all bg-white group"
            data-testid="role-worker"
          >
            <div className="w-12 h-12 bg-brand text-white grid place-items-center mb-6">
              <Wrench size={24} weight="bold" />
            </div>
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-2">For Pros</div>
            <h3 className="font-display text-2xl mb-2">I&apos;m a skilled pro</h3>
            <p className="text-sm text-slate-600 mb-6">Receive qualified leads, build your reputation, and grow your business.</p>
            <div className="flex items-center gap-2 text-sm font-semibold text-brand group-hover:gap-3 transition-all">
              Continue <ArrowRight size={16} weight="bold" />
            </div>
          </button>
        </div>
      </div>
    </div>
  );
}
